import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useChatContext } from '../contexts/ChatContext';
import { usePermissions } from '../hooks/usePermissions';
import { useToast } from '../contexts/ToastContext';
import { useNavigate } from 'react-router-dom';
import { updatePageMetadata, pageMetadata } from '../utils/pageMetadata';
import { User, Settings, LogOut, ChevronRight, ChevronLeft, Lock, HelpCircle, Info, Edit2, Save, X, Phone, Check, Shield, MessageCircle, Trash2, AlertTriangle, Mail, Send, MapPin, Search, Heart, Instagram, Lightbulb, Calendar, Clock } from 'lucide-react';
import { API_BASE_URL, WHATSAPP_PHONE_NUMBER, CHAT_WHATSAPP_NUMBER, TELEGRAM_BOT_USERNAME } from '../config';

type MenuView = 'main' | 'settings' | 'privacy' | 'editProfile' | 'howTo' | 'helpSupport' | 'about';

export default function Profile() {
  const { authState, logout, updateProfile, refreshUserProfile, deleteAccount, resendVerificationEmail, startVerificationListener } = useAuth();
  const { clearChatHistory: clearChatHistoryContext } = useChatContext();
  const { tier, localLimits } = usePermissions();
  const { showToast } = useToast();
  const navigate = useNavigate();
  
  // Update page metadata on mount
  useEffect(() => {
    updatePageMetadata(pageMetadata.profile.title, pageMetadata.profile.description);
  }, []);
  const [currentView, setCurrentView] = useState<MenuView>('main');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    instagramLink: '',
    tiktokLink: '',
  });
  
  // WhatsApp linking state
  const [showWhatsAppLinking, setShowWhatsAppLinking] = useState(false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState<string | null>(null);
  const whatsappListenerCleanup = useRef<(() => void) | null>(null);
  
  // Telegram linking state
  const [showTelegramLinking, setShowTelegramLinking] = useState(false);
  const telegramListenerCleanup = useRef<(() => void) | null>(null);
  
  // Auto-close modals when verification is detected
  useEffect(() => {
    if (authState.user?.whatsappNumberE164 && showWhatsAppLinking) {
      setShowWhatsAppLinking(false);
      // Cleanup listener when verified
      if (whatsappListenerCleanup.current) {
        whatsappListenerCleanup.current();
        whatsappListenerCleanup.current = null;
      }
    }
    if (authState.user?.telegramNumberE164 && showTelegramLinking) {
      setShowTelegramLinking(false);
      // Cleanup listener when verified
      if (telegramListenerCleanup.current) {
        telegramListenerCleanup.current();
        telegramListenerCleanup.current = null;
      }
    }
  }, [authState.user?.whatsappNumberE164, authState.user?.telegramNumberE164, showWhatsAppLinking, showTelegramLinking]);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      if (whatsappListenerCleanup.current) {
        whatsappListenerCleanup.current();
      }
      if (telegramListenerCleanup.current) {
        telegramListenerCleanup.current();
      }
    };
  }, []);

  // Refresh user profile on mount to get latest email verification status
  useEffect(() => {
    const refreshProfile = async () => {
      try {
        await refreshUserProfile();
      } catch (error) {
        // Failed to refresh user profile
      }
    };
    
    if (authState.user?.userId) {
      refreshProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only
  
  // Debug: Log current emailVerified status
  useEffect(() => {
  }, [authState.user?.emailVerified, authState.user?.providers]);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1);
  const [isDeleting, setIsDeleting] = useState(false);

  // Clear chat history state
  const [showClearChatConfirm, setShowClearChatConfirm] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  // Email opt-in state
  const [emailOptIn, setEmailOptIn] = useState<boolean>(
    authState.user?.profile?.emailOptIn !== false // Default to true if undefined
  );

  // How To Guide expanded state
  const [expandedGuideId, setExpandedGuideId] = useState<string | null>(null);

  // Auto-refresh profile data when component mounts
  useEffect(() => {
    const syncProfileData = async () => {
      if (authState.user?.userId && !authState.user?.lastLoginAt) {
        try {
          const refreshedData = await refreshUserProfile();
        } catch (error) {
        }
      } else if (authState.user?.userId) {
      }
    };

    syncProfileData();
  }, [authState.user?.userId, authState.user?.lastLoginAt, refreshUserProfile]);

  // Auto-close WhatsApp modal when verification is detected via real-time listener
  useEffect(() => {
    if (authState.user?.whatsappNumberE164 && showWhatsAppLinking) {
      
      // Check if it's a Sri Lankan number (+94)
      const isLKNumber = authState.user.whatsappNumberE164.startsWith('+94');
      
      // Show appropriate message based on number type
      if (isLKNumber) {
        setVerificationMessage('⚠️ Verified with Sri Lankan number - Limited access. Contact malintha.levine@gmail.com for full access.');
      } else {
        setVerificationMessage('✅ WhatsApp verified successfully! Full access unlocked!');
      }
      setIsCheckingVerification(false);
      
      // Close modal after 3 seconds (longer for LK numbers to read the message)
      setTimeout(() => {
        setShowWhatsAppLinking(false);
        setVerificationMessage(null);
      }, isLKNumber ? 4000 : 2000);
    }
  }, [authState.user?.whatsappNumberE164, showWhatsAppLinking]);

  // Sync emailOptIn state with user data
  useEffect(() => {
    if (authState.user?.profile?.emailOptIn !== undefined) {
      setEmailOptIn(authState.user.profile.emailOptIn);
    }
  }, [authState.user?.profile?.emailOptIn]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Get display name based on priority: firstName > username > phone
  const getDisplayName = () => {
    const user = authState.user;
    if (!user) return '';
    
    if (user.profile?.firstName) {
      return user.profile.firstName;
    }
    if (user.profile?.username) {
      return user.profile.username;
    }
    return user.phoneE164;
  };

  // Get profile photo or placeholder
  const getProfilePhoto = () => {
    return authState.user?.profile?.profilePhoto || null;
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (error) {
      return 'N/A';
    }
  };

  // Initialize form when entering edit mode
  const handleEdit = () => {
    const profile = authState.user?.profile || {};
    setFormData({
      username: profile.username || '',
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      instagramLink: profile.instagramLink || '',
      tiktokLink: profile.tiktokLink || '',
    });
    setIsEditing(true);
  };

  // Cancel editing
  const handleCancel = () => {
    setIsEditing(false);
  };

  // Save profile
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateProfile(formData);
      setIsEditing(false);
      showToast('Profile updated successfully!', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showToast(`Failed to update profile: ${errorMessage}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle input change
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Generate WhatsApp verification link
  const getWhatsAppVerificationLink = () => {
    if (!authState.user?.email) return '';
    
    const phoneNumber = WHATSAPP_PHONE_NUMBER;
    const email = authState.user.email;
    const message = encodeURIComponent(email);
    
    return `https://wa.me/${phoneNumber}?text=${message}`;
  };

  // Check verification status
  const handleCheckVerification = async () => {
    setIsCheckingVerification(true);
    setVerificationMessage(null);
    
    try {
      const updatedUser = await refreshUserProfile();
      
      // Check if WhatsApp was verified using the returned data
      if (updatedUser.whatsappNumberE164 && updatedUser.providers?.includes('whatsapp')) {
        // Check if it's a Sri Lankan number (+94)
        const isLKNumber = updatedUser.whatsappNumberE164.startsWith('+94');
        
        if (isLKNumber) {
          setVerificationMessage('⚠️ Verified with Sri Lankan number - Limited access. Contact malintha.levine@gmail.com for full access.');
          setTimeout(() => {
            setShowWhatsAppLinking(false);
            setVerificationMessage(null);
          }, 4000);
        } else {
          setVerificationMessage('✅ WhatsApp verified successfully!');
          setTimeout(() => {
            setShowWhatsAppLinking(false);
            setVerificationMessage(null);
          }, 2000);
        }
      } else {
        setVerificationMessage('⏳ Not verified yet. Please send your email via WhatsApp first.');
      }
    } catch (error) {
      setVerificationMessage('❌ Error checking verification status. Please try again.');
    } finally {
      setIsCheckingVerification(false);
    }
  };

  // Handle delete account
  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await deleteAccount();
      // User will be logged out automatically by deleteAccount function
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to delete account: ${errorMessage}`);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setDeleteStep(1);
    }
  };

  const handleClearChatHistory = async () => {
    setIsClearing(true);
    try {
      // Use context function to clear both backend and local state
      await clearChatHistoryContext();
      
      setShowClearChatConfirm(false);
      showToast('Chat history cleared successfully', 'success');
    } catch (error) {
      showToast('Failed to clear chat history', 'error');
    } finally {
      setIsClearing(false);
    }
  };

  const handleEmailOptInToggle = async () => {
    const newValue = !emailOptIn;
    setEmailOptIn(newValue);
    
    try {
      if (authState.isAuthenticated && authState.user?.userId) {
        const response = await fetch(`${API_BASE_URL}/api/auth/profile/${authState.user.userId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ emailOptIn: newValue }),
        });
        
        if (!response.ok) {
          throw new Error('Failed to update email preferences');
        }
        
        showToast(
          newValue ? 'Email communications enabled' : 'Email communications disabled',
          'success'
        );
      }
    } catch (error) {
      setEmailOptIn(!newValue); // Revert on error
      showToast('Failed to update email preferences', 'error');
    }
  };

  if (!authState.user) {
    return (
      <div className="p-4">
        <p>Please log in to view your profile.</p>
      </div>
    );
  }

  // Settings submenu view
  if (currentView === 'settings') {
    return (
      <div className="p-3 md:p-4 max-w-2xl mx-auto pb-24 bg-gray-50 min-h-full">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => setCurrentView('main')}
            className="p-2 -ml-2 hover:bg-white rounded-xl transition-colors"
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>
          <h2 className="text-lg font-bold text-gray-900">Settings</h2>
        </div>

        {/* Menu */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
          {/* Privacy & Security */}
          <button
            onClick={() => setCurrentView('privacy')}
            className="w-full flex items-center gap-3 p-3.5 hover:bg-gray-50 transition-colors"
          >
            <div className="w-9 h-9 rounded-xl bg-[#F0E9F8] flex items-center justify-center">
              <Lock size={18} className="text-[#663399]" />
            </div>
            <span className="flex-1 text-left text-sm font-medium text-gray-800">Privacy & Security</span>
            <ChevronRight size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-gray-400">Hey Sri Lanka v1.0.0</p>
        </div>
      </div>
    );
  }

  // Help & Support submenu view
  if (currentView === 'helpSupport') {
    const supportEmail = 'malintha.levine@gmail.com';
    const whatsappMessage = encodeURIComponent('Hi HeySriLanka, I need help with...');
    const whatsappLink = `https://wa.me/${CHAT_WHATSAPP_NUMBER}?text=${whatsappMessage}`;
    const telegramLink = `https://t.me/${TELEGRAM_BOT_USERNAME}?start=support`;

    return (
      <div className="p-4 max-w-2xl mx-auto pb-24">
        {/* Back button */}
        <button
          onClick={() => setCurrentView('main')}
          className="flex items-center text-gray-900 mb-6 hover:text-gray-700"
        >
          <ChevronLeft size={20} />
          <span className="ml-1">Back</span>
        </button>

        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">Help & Support</h2>
        <p className="text-sm text-gray-500 mb-6">Choose how you'd like to reach us</p>

        <div className="space-y-3">
          {/* Email Support */}
          <a
            href={`mailto:${supportEmail}?subject=Hey Sri Lanka Support Request`}
            className="w-full flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center mr-3">
                <Mail size={20} className="text-orange-600" />
              </div>
              <div className="text-left">
                <div className="font-medium text-gray-800 text-sm md:text-base">Email Support</div>
                <div className="text-xs md:text-sm text-gray-500">{supportEmail}</div>
              </div>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </a>

          {/* WhatsApp Support */}
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center mr-3">
                <MessageCircle size={20} className="text-green-600" />
              </div>
              <div className="text-left">
                <div className="font-medium text-gray-800 text-sm md:text-base">WhatsApp</div>
                <div className="text-xs md:text-sm text-gray-500">Chat with us instantly</div>
              </div>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </a>

          {/* Telegram Support */}
          <a
            href={telegramLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-blue-100 to-sky-100 flex items-center justify-center mr-3">
                <Send size={20} className="text-blue-500" />
              </div>
              <div className="text-left">
                <div className="font-medium text-gray-800 text-sm md:text-base">Telegram</div>
                <div className="text-xs md:text-sm text-gray-500">Message our bot</div>
              </div>
            </div>
            <ChevronRight size={20} className="text-gray-400" />
          </a>
        </div>

        {/* Response Time Info */}
        <div className="mt-6 bg-amber-50 border border-amber-100 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#F0E9F8] flex items-center justify-center flex-shrink-0">
              <Info size={16} className="text-amber-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-800 text-sm">Response Times</h4>
              <p className="text-xs text-gray-600 mt-1">
                We typically respond within 24 hours. For urgent matters, WhatsApp or Telegram is fastest.
              </p>
            </div>
          </div>
        </div>

        {/* Future: In-app Chat placeholder */}
        <div className="mt-4 bg-gray-50 border border-gray-200 border-dashed rounded-xl p-4 opacity-60">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
              <MessageCircle size={16} className="text-gray-400" />
            </div>
            <div>
              <h4 className="font-medium text-gray-500 text-sm">Live Chat</h4>
              <p className="text-xs text-gray-400">Coming soon...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // About submenu view
  if (currentView === 'about') {
    return (
      <div className="p-4 max-w-2xl mx-auto pb-24">
        {/* Back button */}
        <button
          onClick={() => setCurrentView('main')}
          className="flex items-center text-gray-900 mb-6 hover:text-gray-700"
        >
          <ChevronLeft size={20} />
          <span className="ml-1">Back</span>
        </button>

        {/* App Logo & Name */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#F0E9F8] to-[#E0D7FA] rounded-2xl shadow-lg mb-3">
            <img src="/logo.svg" alt="Hey Sri Lanka" className="w-10 h-10" />
          </div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Hey Sri Lanka</h1>
          <p className="text-sm text-gray-500 mt-1">v1.0.0</p>
        </div>

        {/* Mission Statement */}
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-5 mb-4 border border-orange-100">
          <h3 className="font-semibold text-gray-800 mb-2 text-sm md:text-base">Our Mission</h3>
          <p className="text-xs md:text-sm text-gray-700 leading-relaxed">
            Hey Sri Lanka brings cutting-edge AI technology to Sri Lanka tourism, empowering travelers 
            to explore the island on their own terms. We believe every visitor deserves a personalized, 
            authentic experience—free from tourist traps and unfair pricing.
          </p>
        </div>

        {/* What We Offer */}
        <div className="bg-white rounded-2xl p-5 mb-4 border border-gray-100 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-3 text-sm md:text-base">What We Offer</h3>
          <ul className="space-y-2 text-xs md:text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-orange-500 mt-0.5">✦</span>
              <span><strong>AI-Powered Discovery</strong> — Chat with our Sri Lanka-trained AI to uncover hidden gems and local insights</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-500 mt-0.5">✦</span>
              <span><strong>Smart Trip Planning</strong> — Create detailed itineraries with AI-optimized routes and timings</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-500 mt-0.5">✦</span>
              <span><strong>Fair Fare Validator</strong> — Know the real price before you ride, no more getting scammed</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-500 mt-0.5">✦</span>
              <span><strong>Curated Experiences</strong> — Handpicked destinations and experiences across the island</span>
            </li>
          </ul>
        </div>

        {/* Founder Note */}
        <div className="bg-gray-50 rounded-2xl p-5 mb-4 border border-gray-200">
          <h3 className="font-semibold text-gray-800 mb-2 text-sm md:text-base flex items-center gap-2">
            <span>💡</span> From the Founder
          </h3>
          <p className="text-xs md:text-sm text-gray-600 leading-relaxed italic">
            "I built Hey Sri Lanka to bring the future of travel technology to my home country. 
            My vision is simple: empower every traveler to experience Sri Lanka exactly the way they want—authentically, 
            confidently, and without barriers. This is just the beginning."
          </p>
        </div>

        {/* Links */}
        <div className="space-y-2">
          <button
            onClick={() => navigate('/terms')}
            className="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            <span className="text-sm text-gray-700">Terms of Service</span>
            <ChevronRight size={16} className="text-gray-400" />
          </button>
          <button
            onClick={() => navigate('/privacy')}
            className="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            <span className="text-sm text-gray-700">Privacy Policy</span>
            <ChevronRight size={16} className="text-gray-400" />
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-gray-400">© {new Date().getFullYear()} Hey Sri Lanka. All rights reserved.</p>
        </div>
      </div>
    );
  }

  // Privacy & Security submenu view
  if (currentView === 'privacy') {
    return (
      <div className="p-3 md:p-4 max-w-2xl mx-auto pb-24 bg-gray-50 min-h-full">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => setCurrentView('settings')}
            className="p-2 -ml-2 hover:bg-white rounded-xl transition-colors"
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>
          <h2 className="text-lg font-bold text-gray-900">Privacy & Security</h2>
        </div>

        {/* Main Options */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm mb-4">
          {/* Clear Chat History */}
          <button
            onClick={() => setShowClearChatConfirm(true)}
            className="w-full flex items-center gap-3 p-3.5 hover:bg-gray-50 transition-colors border-b border-gray-100"
          >
            <div className="w-9 h-9 rounded-xl bg-[#F0E9F8] flex items-center justify-center">
              <Trash2 size={18} className="text-orange-600" />
            </div>
            <span className="flex-1 text-left text-sm font-medium text-gray-800">Clear Chat History</span>
            <ChevronRight size={18} className="text-gray-400" />
          </button>

          {/* Email Communication Opt-in */}
          <div className="w-full flex items-center gap-3 p-3.5">
            <div className="w-9 h-9 rounded-xl bg-[#F0E9F8] flex items-center justify-center">
              <Mail size={18} className="text-[#663399]" />
            </div>
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-800">Email Updates</span>
              <p className="text-xs text-gray-500">{emailOptIn ? 'Enabled' : 'Disabled'}</p>
            </div>
            <button
              onClick={handleEmailOptInToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                emailOptIn ? 'bg-orange-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  emailOptIn ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Danger Zone */}
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2 px-1">Danger Zone</p>
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-red-100">
          <button
            onClick={() => {
              setShowDeleteConfirm(true);
              setDeleteStep(1);
            }}
            className="w-full flex items-center gap-3 p-3.5 hover:bg-red-50 transition-colors"
          >
            <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center">
              <Trash2 size={18} className="text-red-600" />
            </div>
            <span className="flex-1 text-left text-sm font-medium text-red-600">Delete Account</span>
            <ChevronRight size={18} className="text-red-400" />
          </button>
        </div>

        {/* Clear Chat History Confirmation Modal */}
        {showClearChatConfirm && (
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" 
            onClick={() => setShowClearChatConfirm(false)}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative p-6 pb-4">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-orange-400 to-yellow-400 rounded-t-2xl" />
                
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      Clear Chat History?
                    </h3>
                    <p className="text-sm text-gray-600">
                      This action cannot be undone
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 pb-6">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 mb-4">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    All of your chat conversations will be permanently deleted from your account. This includes:
                  </p>
                  <ul className="mt-2 text-sm text-gray-600 space-y-1 ml-4">
                    <li>• All chat messages</li>
                    <li>• Conversation history</li>
                    <li>• AI responses and suggestions</li>
                  </ul>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  Your saved places, plans, and fare history will not be affected.
                </p>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowClearChatConfirm(false)}
                    disabled={isClearing}
                    className="flex-1 px-5 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 active:scale-95 transition-all duration-150 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleClearChatHistory}
                    disabled={isClearing}
                    className="flex-1 px-5 py-3 bg-gradient-to-r from-orange-600 to-orange-500 text-white font-semibold rounded-xl hover:from-orange-700 hover:to-orange-600 active:scale-95 shadow-lg shadow-orange-500/30 transition-all duration-150 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isClearing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Clearing...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        <span>Clear History</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Account Confirmation Modal */}
        {showDeleteConfirm && (
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" 
            onClick={() => {
              setShowDeleteConfirm(false);
              setDeleteStep(1);
            }}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {deleteStep === 1 ? (
                <>
                  {/* Step 1: Initial Warning */}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">
                        Delete Account?
                      </h3>
                    </div>
                    
                    <p className="text-gray-600 mb-4">
                      Are you sure you want to delete your account? This action will:
                    </p>
                    
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="text-red-500 mt-0.5">•</span>
                        <span>Mark your account for deletion after 30 days</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="text-red-500 mt-0.5">•</span>
                        <span>Remove access to your saved places and plans</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="text-red-500 mt-0.5">•</span>
                        <span>Log you out immediately</span>
                      </li>
                    </ul>

                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-6">
                      <p className="text-sm text-blue-700">
                        <strong>Good news:</strong> You can reactivate your account anytime within 30 days by simply logging in again.
                      </p>
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteStep(1);
                        }}
                        className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => setDeleteStep(2)}
                        className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Step 2: Final Confirmation */}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                        <Trash2 className="w-6 h-6 text-red-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">
                        Final Confirmation
                      </h3>
                    </div>
                    
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200 mb-6">
                      <p className="text-sm text-red-700 font-semibold mb-2">
                        Your profile data will be deleted after 30 days
                      </p>
                      <p className="text-sm text-red-600">
                        You can reactivate your account by logging in again within 30 days. After 30 days, your data will be permanently removed.
                      </p>
                    </div>
                    
                    <p className="text-gray-600 mb-6">
                      Are you absolutely sure you want to proceed with account deletion?
                    </p>
                    
                    <div className="flex gap-3">
                      <button
                        onClick={() => setDeleteStep(1)}
                        disabled={isDeleting}
                        className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all disabled:opacity-50"
                      >
                        Go Back
                      </button>
                      <button
                        onClick={handleDeleteAccount}
                        disabled={isDeleting}
                        className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {isDeleting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Deleting...</span>
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4" />
                            <span>Delete Account</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // How To Guide view
  if (currentView === 'howTo') {
    const guideItems = [
      {
        id: 'chat-map',
        title: '💬 Chat ↔️ Map',
        icon: MessageCircle,
        description: 'Discover Sri Lanka with AI trained on local expertise',
        sections: [
          'Ask any question you want from our AI model trained exclusively on Sri Lanka',
          'Get instant map visualization of desired locations and attractions',
          'Ask about local culture and lesser-known facts about Sri Lanka',
          'Switch between chat and map view seamlessly to explore recommendations'
        ]
      },
      {
        id: 'explore',
        title: '🗺️ Explore',
        icon: Search,
        description: 'Discover places and experiences',
        sections: [
          'Browse curated destinations by category (restaurants, hotels, attractions, experiences)',
          'Click into any place to know everything you need to be aware of before visiting',
          'Browse photos of the interested places to get a feeler',
          'Click heart icon to save your desired places for further planning'
        ]
      },
      {
        id: 'saved',
        title: '❤️ Saved Items',
        icon: Heart,
        description: 'Keep your favorites organized',
        sections: [
          'Save places you want to visit for future reference',
          'Organize by categories (restaurants, accommodations, activities)',
          'Browse saved items in an Instagram-like grid view',
          'Access your saved items anytime from the app'
        ]
      },
      {
        id: 'plan',
        title: '✈️ Plan My Trip',
        icon: MapPin,
        description: 'Create detailed itineraries - you are in full control',
        sections: [
          'Plan day trips and multi-day trips from saved items or browsing the map',
          'HeySriLanka AI curates comprehensive plan based on your itinerary',
          'Save and revisit the itineraries',
          'Request quotes for planned trips - full quote or just driver hire'
        ]
      },
      {
        id: 'services',
        title: '🚗 Services',
        icon: 'tuk-tuk',
        description: 'Transport fare validator - be an informed traveler',
        sections: [
          'Get accurate travel fare estimates from point A to point B',
          'Understand the best transport mode between tuk tuk and taxi',
          'See how the fare changes with weather, tourist locations and time of the day',
          'You are informed - no chance of getting scammed'
        ]
      },
      {
        id: 'featured',
        title: '📸 Sri Lankan Gems',
        icon: Instagram,
        description: 'Curated content for inspiration',
        sections: [
          'View featured destinations and experiences',
          'Discover Sri Lanka in a different lens',
          'Get inspired by photos and recommendations of fellow travelers',
          'Follow for daily inspiration and tips'
        ]
      }
    ];

    return (
      <div className="p-3 md:p-4 max-w-2xl mx-auto pb-24 bg-gray-50 min-h-full">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => setCurrentView('main')}
            className="p-2 -ml-2 hover:bg-white rounded-xl transition-colors"
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-gray-900">How To Guide</h2>
            <p className="text-xs text-gray-500">Learn each feature</p>
          </div>
        </div>

        {/* Guide Items */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm mb-4">
          {guideItems.map((item, index) => {
            const isExpanded = expandedGuideId === item.id;
            const isLast = index === guideItems.length - 1;

            return (
              <div key={item.id}>
                {/* Header - Always visible and clickable */}
                <button
                  onClick={() => setExpandedGuideId(expandedGuideId === item.id ? null : item.id)}
                  className={`w-full flex items-center gap-3 p-3.5 hover:bg-gray-50 transition-colors ${!isLast && !isExpanded ? 'border-b border-gray-100' : ''}`}
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                    item.id === 'chat-map' ? 'bg-purple-100' :
                    item.id === 'explore' ? 'bg-[#F0E9F8]' :
                    item.id === 'saved' ? 'bg-[#F0E9F8]' :
                    item.id === 'plan' ? 'bg-[#F0E9F8]' :
                    item.id === 'services' ? 'bg-[#F0E9F8]' :
                    'bg-[#F0E9F8]'
                  }`}>
                    {item.icon === 'tuk-tuk' ? (
                      <img src="/tuk-tuk.png" alt="Tuk Tuk" className="w-5 h-5" />
                    ) : (
                      React.createElement(item.icon, { size: 18, className: `${
                        item.id === 'chat-map' ? 'text-purple-600' :
                        item.id === 'explore' ? 'text-blue-600' :
                        item.id === 'saved' ? 'text-pink-600' :
                        item.id === 'plan' ? 'text-green-600' :
                        item.id === 'services' ? 'text-amber-600' :
                        'text-orange-600'
                      }` })
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium text-gray-800">{item.title.substring(2)}</div>
                    <div className="text-xs text-gray-500">{item.description}</div>
                  </div>
                  <ChevronRight
                    size={18}
                    className={`text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                  />
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className={`bg-gray-50 px-4 py-3 ${!isLast ? 'border-b border-gray-100' : ''}`}>
                    <ul className="space-y-2">
                      {item.sections.map((section, idx) => (
                        <li key={idx} className="flex gap-2">
                          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-purple-400 to-blue-400 flex items-center justify-center mt-0.5">
                            <span className="text-white text-[10px] font-bold">{idx + 1}</span>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed">{section}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Quick Tips */}
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-4 border border-purple-100">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb size={16} className="text-purple-600" />
            <h3 className="text-sm font-semibold text-gray-800">Quick Tips</h3>
          </div>
          <ul className="text-xs text-gray-600 space-y-1.5">
            <li>• Use chat first to explore and get AI recommendations</li>
            <li>• Save interesting places for your trip planning later</li>
            <li>• Request quotes when you're ready to book services</li>
            <li>• Use "I'm too lazy" quick quote for hassle-free requests</li>
            <li>• Use fare validator before you book a tuk tuk or taxi</li>
          </ul>
        </div>
      </div>
    );
  }

  // Edit Profile view
  if (currentView === 'editProfile') {
    return (
      <div className="p-4 max-w-2xl mx-auto pb-44">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => {
              setCurrentView('main');
              setIsEditing(false);
            }}
            className="flex items-center text-gray-900 hover:text-gray-700"
          >
            <ChevronLeft size={20} />
            <span className="ml-1">Back</span>
          </button>
          
          {!isEditing && (
            <button
              onClick={handleEdit}
              className="flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Edit2 size={16} className="mr-2" />
              Edit
            </button>
          )}
        </div>

        <h2 className="text-2xl font-bold text-gray-800 mb-6">Profile Information</h2>

        {/* Helper: Check if WhatsApp number is Sri Lankan (+94) */}
        {(() => {
          const isLKWhatsAppNumber = authState.user.whatsappNumberE164?.startsWith('+94');
          const isLKTelegramNumber = authState.user.telegramNumberE164?.startsWith('+94');
          const hasLKNumberOnly = (isLKWhatsAppNumber || isLKTelegramNumber) && 
            !authState.user.whatsappNumberE164?.match(/^\+(?!94)/) && 
            !authState.user.telegramNumberE164?.match(/^\+(?!94)/);
          
          return (
            <>
              {/* LK Number Limited Access Warning */}
              {hasLKNumberOnly && !showWhatsAppLinking && !showTelegramLinking && (
                <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-300 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
                  <div className="flex items-start">
                    <AlertTriangle size={16} className="md:size-5 text-amber-600 mr-2 md:mr-3 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-sm md:text-base font-semibold text-gray-800 mb-0.5 md:mb-1">Verified with Sri Lankan Number</h3>
                      <p className="text-xs md:text-sm text-gray-600 mb-2">
                        Your account has limited access because you verified with a Sri Lankan number (+94).
                      </p>
                      <p className="text-xs md:text-sm text-gray-600">
                        If you have a foreign number (your home country), please contact{' '}
                        <a href="mailto:malintha.levine@gmail.com" className="text-amber-700 font-semibold hover:underline">
                          malintha.levine@gmail.com
                        </a>
                        {' '}to change your number and unlock full access.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Verification Section - Show if neither WhatsApp nor Telegram is verified (excluding LK numbers) */}
              {!authState.user.whatsappNumberE164 && !authState.user.telegramNumberE164 && !showWhatsAppLinking && !showTelegramLinking && (
                <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 border border-orange-200 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
                  <div className="flex items-start">
                    <MessageCircle size={16} className="md:size-5 text-orange-600 mr-2 md:mr-3 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-sm md:text-base font-semibold text-gray-800 mb-0.5 md:mb-1">Unlock Full Access</h3>
                      <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-3">
                        Verify via WhatsApp or Telegram for unlimited chats, fares, and plans
                      </p>
                      <div className="flex gap-2 md:gap-3">
                        <button
                          onClick={() => {
                            setShowWhatsAppLinking(true);
                            // Start on-demand listener when user initiates verification
                            whatsappListenerCleanup.current = startVerificationListener('whatsapp');
                          }}
                          className="flex-1 flex items-center justify-center gap-1 md:gap-2 px-2 py-2 md:px-4 md:py-3 bg-green-600 text-white text-sm md:text-base font-semibold rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <MessageCircle size={14} className="md:w-[18px] md:h-[18px]" />
                          <span>Verify WhatsApp</span>
                        </button>
                        <button
                          onClick={() => {
                            setShowTelegramLinking(true);
                            // Start on-demand listener when user initiates verification
                            telegramListenerCleanup.current = startVerificationListener('telegram');
                          }}
                          className="flex-1 flex items-center justify-center gap-1 md:gap-2 px-2 py-2 md:px-4 md:py-3 bg-blue-500 text-white text-sm md:text-base font-semibold rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          <Send size={14} className="md:w-[18px] md:h-[18px]" />
                          <span>Verify Telegram</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* WhatsApp Verification Modal */}
        {showWhatsAppLinking && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-semibold text-gray-800">WhatsApp Verification</h3>
              <button onClick={() => {
                setShowWhatsAppLinking(false);
                // Cleanup listener when modal is manually closed
                if (whatsappListenerCleanup.current) {
                  whatsappListenerCleanup.current();
                  whatsappListenerCleanup.current = null;
                }
              }}>
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>How it works:</strong>
                </p>
                <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                  <li>Click the button below to open WhatsApp</li>
                  <li>Your email will be pre-filled: {authState.user?.email}</li>
                  <li>Send the message to verify your WhatsApp number</li>
                </ol>
              </div>
              
              <a
                href={getWhatsAppVerificationLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-green-600 hover:bg-green-700 text-white text-center text-sm md:text-base font-semibold py-2 md:py-3 px-4 md:px-6 rounded-lg transition-colors"
              >
                Open WhatsApp to Verify
              </a>
              
              <p className="text-xs text-gray-500 text-center">
                Make sure to send the message from the WhatsApp account you want to link
              </p>

              {/* Check Verification Status Button */}
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm text-gray-600 mb-3 text-center">
                  After sending your email via WhatsApp, click below to check if verification is complete:
                </p>
                <button
                  onClick={handleCheckVerification}
                  disabled={isCheckingVerification}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm md:text-base font-semibold py-2 md:py-3 px-4 md:px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {isCheckingVerification ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Checking...
                    </>
                  ) : (
                    <>
                      <Check size={20} />
                      Check Verification Status
                    </>
                  )}
                </button>
                
                {/* Verification Message */}
                {verificationMessage && (
                  <div className={`mt-3 p-3 rounded-lg text-sm text-center ${
                    verificationMessage.startsWith('✅') 
                      ? 'bg-green-50 text-green-800 border border-green-200' 
                      : verificationMessage.startsWith('⏳')
                      ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                    {verificationMessage}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Telegram Verification Modal */}
        {showTelegramLinking && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-semibold text-gray-800">Telegram Verification</h3>
              <button onClick={() => {
                setShowTelegramLinking(false);
                // Cleanup listener when modal is manually closed
                if (telegramListenerCleanup.current) {
                  telegramListenerCleanup.current();
                  telegramListenerCleanup.current = null;
                }
              }}>
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>How it works:</strong>
                </p>
                <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                  <li>Click the button below to open Telegram to HeySriLankabot</li>
                  <li>Once you are in Telegram click "Start" or just send 'Hey' to begin verification</li>
                  <li>You then need to send the email used to register with HeySriLanka</li>
                  <li>Your email will be verified automatically</li>
                  <li>Tap the "Share Phone Number" button to complete</li>
                </ol>
              </div>
              
              <a
                href={`https://t.me/${TELEGRAM_BOT_USERNAME}?start=${authState.user?.email || ''}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-blue-500 hover:bg-blue-600 text-white text-center text-sm md:text-base font-semibold py-2 md:py-3 px-4 md:px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Send size={16} className="md:w-5 md:h-5" />
                Open Telegram to Verify
              </a>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> Make sure to send your registered email to the bot. After email verification, 
                  you'll be asked to share your phone number. Your profile will update automatically once verified.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Account Status / Tier */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200 p-4 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-2">
                <Shield size={16} />
                Account Status
              </h3>
              
              {tier === 'unregistered' && (
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-semibold">
                    <User size={14} />
                    Free User - Limited Access
                  </div>
                  {localLimits && (
                    <div className="text-xs text-gray-600 mt-2">
                      <div>Chats used: {localLimits.chats} / 5</div>
                      <div>Fare checks used: {localLimits.fares} / 2</div>
                    </div>
                  )}
                  <p className="text-sm text-gray-600 mt-2">
                    Sign in with Google or Email to unlock more features
                  </p>
                </div>
              )}
              
              {tier === 'google_auth' && (
                <div className="space-y-2">
                  {authState.user?.providers?.includes('google') ? (
                    <div className="inline-flex items-center gap-2 bg-[#F0E9F8] text-[#9A79BA] px-3 py-1 rounded-full text-sm font-semibold">
                      <Shield size={14} />
                      Google Verified
                    </div>
                  ) : authState.user?.providers?.includes('email') ? (
                    <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold">
                      <Shield size={14} />
                      Registered with Email
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-2 bg-[#F0E9F8] text-[#9A79BA] px-3 py-1 rounded-full text-sm font-semibold">
                      <Shield size={14} />
                      Verified Account
                    </div>
                  )}
                  <p className="text-sm text-gray-600 mt-2">
                    10 chats/hour • 2 fare checks/hour • Save places • Trip planning
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-sm text-orange-700 bg-orange-50 px-3 py-2 rounded-lg">
                    <MessageCircle size={16} />
                    <span>Verify WhatsApp or Telegram for unlimited access</span>
                  </div>
                </div>
              )}
              
              {tier === 'whatsapp_verified' && (
                <div className="space-y-2">
                    <div className="inline-flex items-center gap-2 bg-[#F0E9F8] text-[#9A79BA] px-3 py-1 rounded-full text-sm font-semibold">
                    <Check size={14} />
                    Full Access
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Unlimited chats • Unlimited fare checks • Save places • Advanced trip planning
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Account Information */}
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2 px-1">Account Information</p>
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm mb-4">
          {/* Phone Number or Email */}
          {authState.user.phoneE164 ? (
            <div className="flex items-center gap-3 p-3.5 border-b border-gray-100">
              <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center">
                <MessageCircle size={18} className="text-[#663399]" />
              </div>
              <div className="flex-1">
                <span className="text-xs text-gray-500">Phone Number</span>
                <p className="text-sm font-medium text-gray-800">{authState.user.phoneE164}</p>
              </div>
            </div>
          ) : authState.user.email ? (
            <div className="flex items-center gap-3 p-3.5 border-b border-gray-100">
              <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center">
                <Mail size={18} className="text-gray-600" />
              </div>
              <div className="flex-1">
                <span className="text-xs text-gray-500">Email</span>
                <p className="text-sm font-medium text-gray-800">{authState.user.email}</p>
              </div>
            </div>
          ) : null}

          {/* WhatsApp Verification Status */}
          {authState.user.whatsappNumberE164 && authState.user.providers?.includes('whatsapp') && (
            <div className="flex items-center gap-3 p-3.5 border-b border-gray-100">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                authState.user.whatsappNumberE164.startsWith('+94') ? 'bg-[#F0E9F8]' : 'bg-[#F0E9F8]'
              }`}>
                <MessageCircle size={18} className={
                  authState.user.whatsappNumberE164.startsWith('+94') ? 'text-amber-600' : 'text-green-600'
                } />
              </div>
              <div className="flex-1">
                <span className="text-xs text-gray-500">WhatsApp</span>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-gray-800">{authState.user.whatsappNumberE164}</p>
                  {authState.user.whatsappNumberE164.startsWith('+94') ? (
                    <span className="inline-flex items-center gap-1 bg-[#F0E9F8] text-[#9A79BA] px-1.5 py-0.5 rounded-full text-[10px] font-semibold">
                      <AlertTriangle size={10} />
                      Limited Access
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 bg-[#F0E9F8] text-[#9A79BA] px-1.5 py-0.5 rounded-full text-[10px] font-semibold">
                      <Check size={10} />
                      Verified
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Joined Date */}
          <div className="flex items-center gap-3 p-3.5 border-b border-gray-100">
            <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center">
              <Calendar size={18} className="text-[#663399]" />
            </div>
            <div className="flex-1">
              <span className="text-xs text-gray-500">Joined</span>
              <p className="text-sm font-medium text-gray-800">{formatDate(authState.user.createdAt)}</p>
            </div>
          </div>

          {/* Last Active */}
          <div className="flex items-center gap-3 p-3.5">
            <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center">
              <Clock size={18} className="text-[#663399]" />
            </div>
            <div className="flex-1">
              <span className="text-xs text-gray-500">Last Active</span>
              <p className="text-sm font-medium text-gray-800">{formatDate(authState.user.lastLoginAt)}</p>
            </div>
          </div>
        </div>

        {/* Personal Details */}
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2 px-1">Personal Details</p>
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
          {/* Username */}
          <div className="p-3.5 border-b border-gray-100">
            <label className="block text-xs text-gray-500 mb-1">Username</label>
            <input
              type="text"
              value={isEditing ? formData.username : (authState.user.profile?.username || 'Not set')}
              onChange={(e) => handleInputChange('username', e.target.value)}
              disabled={!isEditing}
              placeholder="Enter username"
              className={`w-full text-sm font-medium ${
                isEditing
                  ? 'px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700'
                  : 'text-gray-800 bg-transparent'
              }`}
            />
          </div>

          {/* First Name */}
          <div className="p-3.5 border-b border-gray-100">
            <label className="block text-xs text-gray-500 mb-1">First Name</label>
            <input
              type="text"
              value={isEditing ? formData.firstName : (authState.user.profile?.firstName || 'Not set')}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              disabled={!isEditing}
              placeholder="Enter first name"
              className={`w-full text-sm font-medium ${
                isEditing
                  ? 'px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700'
                  : 'text-gray-800 bg-transparent'
              }`}
            />
          </div>

          {/* Last Name */}
          <div className="p-3.5 border-b border-gray-100">
            <label className="block text-xs text-gray-500 mb-1">Last Name</label>
            <input
              type="text"
              value={isEditing ? formData.lastName : (authState.user.profile?.lastName || 'Not set')}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              disabled={!isEditing}
              placeholder="Enter last name"
              className={`w-full text-sm font-medium ${
                isEditing
                  ? 'px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700'
                  : 'text-gray-800 bg-transparent'
              }`}
            />
          </div>

          {/* Instagram Link */}
          <div className="p-3.5 border-b border-gray-100">
            <label className="block text-xs text-gray-500 mb-1">Instagram</label>
            <input
              type="text"
              value={isEditing ? formData.instagramLink : (authState.user.profile?.instagramLink || 'Not set')}
              onChange={(e) => handleInputChange('instagramLink', e.target.value)}
              disabled={!isEditing}
              placeholder="@username or profile link"
              className={`w-full text-sm font-medium ${
                isEditing
                  ? 'px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700'
                  : 'text-gray-800 bg-transparent'
              }`}
            />
          </div>

          {/* TikTok Link */}
          <div className="p-3.5">
            <label className="block text-xs text-gray-500 mb-1">TikTok</label>
            <input
              type="text"
              value={isEditing ? formData.tiktokLink : (authState.user.profile?.tiktokLink || 'Not set')}
              onChange={(e) => handleInputChange('tiktokLink', e.target.value)}
              disabled={!isEditing}
              placeholder="@username or profile link"
              className={`w-full text-sm font-medium ${
                isEditing
                  ? 'px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700'
                  : 'text-gray-800 bg-transparent'
              }`}
            />
          </div>
        </div>

        {/* Action Buttons - Fixed at bottom when editing */}
        {isEditing && (
          <div className="fixed bottom-16 left-0 right-0 p-3 bg-white border-t border-gray-100 flex gap-2 shadow-sm">
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <X size={16} />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              <Save size={16} />
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Main profile view
  return (
    <div className="p-3 md:p-4 max-w-2xl mx-auto pb-24 bg-gray-50 min-h-full">
      {/* Profile Card - Clickable */}
      <button
        onClick={() => setCurrentView('editProfile')}
        className="w-full bg-white rounded-2xl p-4 mb-4 shadow-sm hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-4">
          {/* Profile Photo */}
          <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-orange-400 to-amber-500 rounded-2xl flex items-center justify-center overflow-hidden shadow-lg flex-shrink-0">
            {getProfilePhoto() ? (
              <img
                src={getProfilePhoto()!}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <User size={28} className="text-white" />
            )}
          </div>
          
          {/* User Info */}
          <div className="flex-1 min-w-0">
            <h1 className="text-lg md:text-xl font-bold text-gray-900 truncate">{getDisplayName()}</h1>
            <p className="text-xs md:text-sm text-gray-500 truncate">
              {authState.user.profile?.email || authState.user.phoneE164 || 'Welcome to Hey Sri Lanka'}
            </p>
          </div>
          
          {/* View Text */}
          <span className="text-xs text-gray-400 font-medium">View</span>
        </div>
      </button>

      {/* Email Verification Banner */}
      {!authState.user.emailVerified && authState.user.providers?.includes('email') && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-800 flex-1">
              Verify your email to secure your account.{' '}
              <button
                onClick={async () => {
                  try {
                    await resendVerificationEmail();
                  } catch (error) {
                  }
                }}
                className="font-semibold underline"
              >
                Resend link
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Menu Grid */}
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm mb-4">
        {/* How To */}
        <button
          onClick={() => setCurrentView('howTo')}
          className="w-full flex items-center gap-3 p-3.5 hover:bg-gray-50 transition-colors border-b border-gray-100"
        >
          <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center">
            <Lightbulb size={18} className="text-purple-600" />
          </div>
          <span className="flex-1 text-left text-sm font-medium text-gray-800">How To Guide</span>
          <ChevronRight size={18} className="text-gray-400" />
        </button>

        {/* Settings */}
        <button
          onClick={() => setCurrentView('settings')}
          className="w-full flex items-center gap-3 p-3.5 hover:bg-gray-50 transition-colors border-b border-gray-100"
        >
            <div className="w-9 h-9 rounded-xl bg-[#F0E9F8] flex items-center justify-center">
              <Settings size={18} className="text-[#663399]" />
          </div>
          <span className="flex-1 text-left text-sm font-medium text-gray-800">Settings</span>
          <ChevronRight size={18} className="text-gray-400" />
        </button>

        {/* Help & Support */}
        <button
          onClick={() => setCurrentView('helpSupport')}
          className="w-full flex items-center gap-3 p-3.5 hover:bg-gray-50 transition-colors border-b border-gray-100"
        >
          <div className="w-9 h-9 rounded-xl bg-[#F0E9F8] flex items-center justify-center">
            <MessageCircle size={18} className="text-[#663399]" />
          </div>
          <span className="flex-1 text-left text-sm font-medium text-gray-800">Help & Support</span>
          <ChevronRight size={18} className="text-gray-400" />
        </button>

        {/* About */}
        <button
          onClick={() => setCurrentView('about')}
          className="w-full flex items-center gap-3 p-3.5 hover:bg-gray-50 transition-colors"
        >
          <div className="w-9 h-9 rounded-xl bg-[#F0E9F8] flex items-center justify-center">
            <Info size={18} className="text-orange-600" />
          </div>
          <span className="flex-1 text-left text-sm font-medium text-gray-800">About</span>
          <ChevronRight size={18} className="text-gray-400" />
        </button>

        {/* Admin Panel - Only visible if user is admin */}
        {authState.user?.email === 'accounts@heysrilanka.com' && authState.user?.emailVerified && (
          <a
            href="/adminlord"
            className="w-full flex items-center gap-3 p-3.5 hover:bg-gray-50 transition-colors border-t border-gray-200"
          >
            <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center">
              <Shield size={18} className="text-purple-600" />
            </div>
            <span className="flex-1 text-left text-sm font-medium text-gray-800">Admin Panel</span>
            <ChevronRight size={18} className="text-gray-400" />
          </a>
        )}
      </div>

      {/* Logout Button */}
      <button
        onClick={handleLogout}
        className="w-full flex items-center justify-center gap-2 p-3.5 bg-white rounded-2xl shadow-sm hover:bg-red-50 transition-colors group"
      >
        <LogOut size={18} className="text-red-500 group-hover:text-red-600" />
        <span className="text-sm font-medium text-red-500 group-hover:text-red-600">Log Out</span>
      </button>

      {/* Footer */}
      <div className="text-center mt-6">
        <p className="text-xs text-gray-400">Hey Sri Lanka v1.0.0</p>
      </div>

      {/* Clear Chat History Confirmation Modal */}
      {showClearChatConfirm && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" 
          onClick={() => setShowClearChatConfirm(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative p-6 pb-4">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-orange-400 to-yellow-400 rounded-t-2xl" />
              
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    Clear Chat History?
                  </h3>
                  <p className="text-sm text-gray-600">
                    This action cannot be undone
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 pb-6">
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 mb-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  All of your chat conversations will be permanently deleted from your account. This includes:
                </p>
                <ul className="mt-2 text-sm text-gray-600 space-y-1 ml-4">
                  <li>• All chat messages</li>
                  <li>• Conversation history</li>
                  <li>• AI responses and suggestions</li>
                </ul>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Your saved places, trips, and fare history will not be affected.
              </p>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowClearChatConfirm(false)}
                  disabled={isClearing}
                  className="flex-1 px-5 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 active:scale-95 transition-all duration-150 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearChatHistory}
                  disabled={isClearing}
                  className="flex-1 px-5 py-3 bg-gradient-to-r from-orange-600 to-orange-500 text-white font-semibold rounded-xl hover:from-orange-700 hover:to-orange-600 active:scale-95 shadow-lg shadow-orange-500/30 transition-all duration-150 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isClearing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Clearing...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>Clear History</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" 
          onClick={() => {
            setShowDeleteConfirm(false);
            setDeleteStep(1);
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {deleteStep === 1 ? (
              <>
                {/* Step 1: Initial Warning */}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-red-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Delete Account?
                    </h3>
                  </div>
                  
                  <p className="text-gray-600 mb-4">
                    Are you sure you want to delete your account? This action will:
                  </p>
                  
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="text-red-500 mt-0.5">•</span>
                      <span>Mark your account for deletion after 30 days</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="text-red-500 mt-0.5">•</span>
                      <span>Remove access to your saved places and trips</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="text-red-500 mt-0.5">•</span>
                      <span>Log you out immediately</span>
                    </li>
                  </ul>

                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-6">
                    <p className="text-sm text-blue-700">
                      <strong>Good news:</strong> You can reactivate your account anytime within 30 days by simply logging in again.
                    </p>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteStep(1);
                      }}
                      className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setDeleteStep(2)}
                      className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Step 2: Final Confirmation */}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <Trash2 className="w-6 h-6 text-red-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Final Confirmation
                    </h3>
                  </div>
                  
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200 mb-6">
                    <p className="text-sm text-red-700 font-semibold mb-2">
                      Your profile data will be deleted after 30 days
                    </p>
                    <p className="text-sm text-red-600">
                      You can reactivate your account by logging in again within 30 days. After 30 days, your data will be permanently removed.
                    </p>
                  </div>
                  
                  <p className="text-gray-600 mb-6">
                    Are you absolutely sure you want to proceed with account deletion?
                  </p>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => setDeleteStep(1)}
                      disabled={isDeleting}
                      className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all disabled:opacity-50"
                    >
                      Go Back
                    </button>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={isDeleting}
                      className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isDeleting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          <span>Delete Account</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
